import { cssVars } from '@lmc/design-system/tokens'
import { Button, Divider, Heading, Text, Warning } from '@lmc/ui-kit'
import { Modal } from 'antd'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type AddMedicationConfirmModalProps = {
  open: boolean
  title: string
  body: string
  /** "Go back" when leaving the page, "Cancel" when undoing an edit. */
  cancelLabel: string
  /** Red Confirm, for overwriting data that came from the NHS. */
  isDanger?: boolean
  onConfirm: () => void
  /** The cancel button, the close button and Escape all land here. */
  onCancel: () => void
}

/** "Are you sure?", laid out like the other eMAR V2 warning modals. */
export const AddMedicationConfirmModal = ({
  open,
  title,
  body,
  cancelLabel,
  isDanger = false,
  onConfirm,
  onCancel
}: AddMedicationConfirmModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={MODAL_WIDTH}
      title={
        <Title>
          <TitleIcon>
            <Warning size={24} color="currentColor" />
          </TitleIcon>
          <TitleText>
            <Heading level={3} weight="medium">
              {title}
            </Heading>
          </TitleText>
        </Title>
      }
    >
      <Divider />
      <Body>
        <Text>{body}</Text>
      </Body>
      <Divider />
      <Footer>
        <Button variant="default" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button variant={isDanger ? 'danger' : 'primary'} onClick={onConfirm}>
          {t(emarV2Constants.CONFIRM)}
        </Button>
      </Footer>
    </Modal>
  )
}

/** As designed: a 599px body inside 24px padding. */
const MODAL_WIDTH = 647

const { color, spacing } = cssVars

const Title = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing[100]};
`

const TitleIcon = styled.span`
  display: inline-flex;
  color: ${color.icon.status.warning.bold};
`

/** Sentence case, as designed: the legacy ui-kit Modal.less uppercases titles. */
const TitleText = styled.span`
  text-transform: none;
`

const Body = styled.div`
  padding: ${spacing[300]} 0;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing[100]};
  margin-top: ${spacing[300]};
`
